import React, { useCallback, useEffect, useState } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { FaExclamationCircle } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import '../css/financeiro-parcela-pagar.css';

const TITULO = 'Mais de uma sede nesta rede';

const nomeDe = (sede) => sede.nomefantasia || sede.nome || '';

// Um popover aberto por vez na tela, como no PillObservacao do aéreo: o gatilho para a propagação
// do clique (vive dentro de cabeçalho que abre e fecha), então o `rootClose` do que está aberto não
// fica sabendo do clique no gatilho de outro. Quem abre fecha o anterior.
let fecharOAberto = null;
function usePopoverUnico() {
    const [aberto, setAberto] = useState(false);
    const fechar = useCallback(() => setAberto(false), []);
    const alternar = (abrir) => {
        if (abrir) {
            if (fecharOAberto && fecharOAberto !== fechar) fecharOAberto();
            fecharOAberto = fechar;
        } else if (fecharOAberto === fechar) {
            fecharOAberto = null;
        }
        setAberto(abrir);
    };
    useEffect(() => () => { if (fecharOAberto === fechar) fecharOAberto = null; }, [fechar]);
    return [aberto, alternar];
}

/**
 * Selo "N redes relacionadas": as outras redes em que a sede desta rede também é sede — ou, na
 * linha do cadastro do grupo, as outras redes do fornecedor. O clique abre o mesmo popover do
 * aviso de sedes, com cada rede e o link do cadastro dela.
 *
 * @param {Array}  relacionadas  { id, nome, sede? } — `sede` é o nome da sede em comum, quando há
 * @param {string} titulo        opcional — o título do popover
 */
export function RedesRelacionadas({ relacionadas, titulo = 'Redes com a mesma sede' }) {
    const [aberto, alternar] = usePopoverUnico();
    const lista = relacionadas || [];

    if (lista.length === 0) return null;

    const rotulo = `${lista.length} rede${lista.length !== 1 ? 's' : ''} relacionada${lista.length !== 1 ? 's' : ''}`;

    const popover = (
        <Popover id="redes-relacionadas" className="fin-pp" style={{ width: 320, maxWidth: 'calc(100vw - 32px)' }}>
            <Popover.Title as="h3">{titulo}</Popover.Title>
            <Popover.Content style={{ fontSize: 13, color: 'var(--fin-tx)' }}>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {lista.map(rede => (
                        <li key={rede.id}>
                            <Link to={'/cadastroFornecedorGrupo/' + rede.id}>{rede.nome}</Link>
                            {rede.sede && <span style={{ color: 'var(--fin-mt)' }}> — sede {rede.sede}</span>}
                        </li>
                    ))}
                </ul>
            </Popover.Content>
        </Popover>
    );

    return (
        <span onClick={e => e.stopPropagation()} style={{ display: 'inline-flex' }}>
            <OverlayTrigger
                trigger="click"
                placement="bottom-start"
                rootClose
                container={document.body}
                show={aberto}
                onToggle={alternar}
                overlay={popover}
            >
                <button
                    type="button"
                    className="fin-pp__minitag is-info"
                    title="Ver as redes com a mesma sede"
                    style={{ fontFamily: 'inherit', cursor: 'pointer' }}
                >
                    {rotulo}
                </button>
            </OverlayTrigger>
        </span>
    );
}

/**
 * Aviso de rede com mais de uma sede: um (!) vermelho no molde da pílula de observação do RSVP e do
 * aéreo. Com menos de duas sedes não renderiza nada.
 *
 * Onde a sede se edita — o cadastro do grupo — o clique chama `onAbrir`, que abre o modal das
 * sedes. Nas outras telas o clique abre um popover só de leitura, com as sedes e o caminho para o
 * cadastro do grupo.
 *
 * @param {Array}    sedes    membros marcados como sede: { id, nome, nomefantasia }
 * @param {Function} onAbrir  opcional — quem edita abre o próprio modal
 * @param {string}   grupoId  opcional — liga o popover ao cadastro do grupo
 */
export default function AlertaSedes({ sedes, onAbrir, grupoId }) {
    const [aberto, alternar] = usePopoverUnico();
    const lista = sedes || [];

    if (lista.length < 2) return null;

    // O (!) cru, sem pílula em volta — como o (i) da observação no RSVP: o círculo é o do ícone.
    const botao = (
        <button
            type="button"
            title={TITULO}
            aria-label={TITULO}
            style={{
                display: 'inline-flex', alignItems: 'center', padding: 0, border: 0, lineHeight: 0,
                background: 'transparent', color: 'var(--fin-dg)', cursor: 'pointer', flexShrink: 0,
            }}
            onClick={onAbrir ? () => onAbrir() : undefined}
        >
            <FaExclamationCircle size={15} />
        </button>
    );

    if (onAbrir) {
        return <span onClick={e => e.stopPropagation()} style={{ display: 'inline-flex' }}>{botao}</span>;
    }

    const popover = (
        <Popover id="sedes-da-rede" className="fin-pp" style={{ width: 300, maxWidth: 'calc(100vw - 32px)' }}>
            <Popover.Title as="h3">{TITULO}</Popover.Title>
            <Popover.Content style={{ fontSize: 13, color: 'var(--fin-tx)' }}>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {lista.map(sede => <li key={sede.id}>{nomeDe(sede)}</li>)}
                </ul>
                {grupoId && (
                    <Link to={'/cadastroFornecedorGrupo/' + grupoId} style={{ display: 'inline-block', marginTop: 8 }}>
                        Ajustar no cadastro do grupo
                    </Link>
                )}
            </Popover.Content>
        </Popover>
    );

    return (
        <span onClick={e => e.stopPropagation()} style={{ display: 'inline-flex' }}>
            <OverlayTrigger
                trigger="click"
                placement="bottom-start"
                rootClose
                container={document.body}
                show={aberto}
                onToggle={alternar}
                overlay={popover}
            >
                {botao}
            </OverlayTrigger>
        </span>
    );
}
